import type { Meta, Reference } from "@medplum/fhirtypes";
import type { ResourceRef } from "@vibrant/models/fhir/fhir-resource-types";
import { toResourceRef } from "@vibrant/models/fhir/fhir-resource-utils";

/**
 * Medplum-specific extensions to FHIR `Meta` used for multi-tenant access control.
 *
 * Notes:
 * - `Meta.account` is legacy and `Meta.accounts` is preferred.
 * - These are not part of the FHIR R4 `Meta` spec; they are Medplum extensions.
 */
export type MedplumMeta = Meta & {
  account?: Reference | undefined;
  accounts?: Reference[] | undefined;
  compartment?: Reference[] | undefined;
};

/**
 * Upserts a reference into a map keyed by `reference`, deduping by `reference` and backfilling
 * `display`, `identifier`, and `type` if we only learn them later.
 *
 * This is intentionally generic so we can reuse it for both:
 * - Medplum account-compartment references (from `meta.account(s)`)
 * - Medplum/our `meta.compartment` arrays (FHIR `Reference`-shaped objects)
 */
export function upsertReferenceByReference<T extends ResourceRef>(
  byReference: Map<string, T>,
  candidate: T,
): void {
  const existing = byReference.get(candidate.reference);
  if (!existing) {
    byReference.set(candidate.reference, candidate);
    return;
  }

  // Prefer the first metadata we see, but backfill it if we only learn it later.
  const shouldUpdateDisplay = !existing.display && candidate.display;
  const shouldUpdateIdentifier = !existing.identifier && candidate.identifier;
  const shouldUpdateType = !existing.type && candidate.type;

  if (!shouldUpdateDisplay && !shouldUpdateIdentifier && !shouldUpdateType) {
    return;
  }

  byReference.set(candidate.reference, {
    ...existing,
    display: shouldUpdateDisplay ? candidate.display : existing.display,
    identifier: shouldUpdateIdentifier ? candidate.identifier : existing.identifier,
    type: shouldUpdateType ? candidate.type : existing.type,
  });
}

export interface ParsedAccountCompartments {
  accounts: ResourceRef[];
  legacyAccount: ResourceRef | undefined;
}

/**
 * Validates Medplum account-compartment references when the caller explicitly trusts the payload.
 *
 * @param meta - The Medplum meta object to validate.
 */
export function assertValidAccountCompartments({ meta }: { meta: MedplumMeta | undefined }): void {
  if (!meta) return;

  if (meta.accounts !== undefined && !Array.isArray(meta.accounts)) {
    throw new InvalidPracticeCompartmentError(
      "Invalid practice compartment: meta.accounts must be an array of Reference objects.",
      {
        organizationReferencesFromAccounts: [],
        legacyOrganizationReference: undefined,
      },
    );
  }

  if (Array.isArray(meta.accounts)) {
    const invalidIndices = meta.accounts
      .map((account, index) => ({
        index,
        isValid: toResourceRef({ ref: account }) !== undefined,
      }))
      .filter((entry) => !entry.isValid)
      .map((entry) => entry.index);

    if (invalidIndices.length > 0) {
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts contains invalid references at indices ${invalidIndices.join(
          ", ",
        )}.`,
        {
          organizationReferencesFromAccounts: [],
          legacyOrganizationReference: undefined,
        },
      );
    }
  }

  if (meta.account !== undefined && !toResourceRef({ ref: meta.account })) {
    throw new InvalidPracticeCompartmentError(
      "Invalid practice compartment: meta.account must be a Reference with a non-empty reference string.",
      {
        organizationReferencesFromAccounts: [],
        legacyOrganizationReference: undefined,
      },
    );
  }
}

/**
 * Extracts Medplum "account compartments" from a resource's `meta`.
 *
 * Supports both `meta.accounts[]` (preferred in Vibrant) and `meta.account` (legacy).
 */
export function parseAccountCompartmentsFromMeta(
  meta: MedplumMeta | undefined,
): ParsedAccountCompartments {
  const accounts = Array.isArray(meta?.accounts)
    ? meta.accounts
        .map((account) => toResourceRef({ ref: account }))
        .filter((account): account is ResourceRef => account !== undefined)
    : [];

  const legacyAccount = toResourceRef({ ref: meta?.account });

  return { accounts, legacyAccount };
}

export class InvalidPracticeCompartmentError extends Error {
  readonly code = "INVALID_PRACTICE_COMPARTMENT" as const;

  constructor(
    message: string,
    readonly details: {
      organizationReferencesFromAccounts: string[];
      legacyOrganizationReference?: string | undefined;
    },
  ) {
    super(message);
    this.name = "InvalidPracticeCompartmentError";
  }
}

/**
 * Returns the single practice `Organization` compartment from Medplum `meta.accounts` / legacy
 * `meta.account`.
 *
 * Note: These fields are Medplum-specific (non-FHIR-standard) and are treated as a hard multi-tenant
 * boundary by access policies. We therefore validate aggressively rather than guessing.
 */
export function getPracticeOrganizationFromMeta(
  meta: MedplumMeta | undefined,
): ResourceRef | undefined {
  const { accounts, legacyAccount } = parseAccountCompartmentsFromMeta(meta);

  const organizationAccounts = getOrganizationAccountsFromAccounts(accounts);
  const legacyOrganizationAccount = legacyAccount?.reference.startsWith("Organization/")
    ? legacyAccount
    : undefined;

  if (organizationAccounts.length > 0) {
    if (organizationAccounts.length > 1) {
      const referencesList = organizationAccounts.map((a) => a.reference).join(", ");
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts contains multiple Organization references (${referencesList}). Expected exactly one.`,
        {
          organizationReferencesFromAccounts: organizationAccounts.map((a) => a.reference),
          legacyOrganizationReference: legacyOrganizationAccount?.reference,
        },
      );
    }

    const fromAccounts = organizationAccounts[0];
    if (
      legacyOrganizationAccount &&
      legacyOrganizationAccount.reference !== fromAccounts.reference
    ) {
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts (${fromAccounts.reference}) does not match legacy meta.account (${legacyOrganizationAccount.reference}).`,
        {
          organizationReferencesFromAccounts: [fromAccounts.reference],
          legacyOrganizationReference: legacyOrganizationAccount.reference,
        },
      );
    }

    return {
      reference: fromAccounts.reference,
      display: fromAccounts.display ?? legacyOrganizationAccount?.display,
    };
  }

  return legacyOrganizationAccount;
}

function getOrganizationAccountsFromAccounts(accounts: ResourceRef[]): ResourceRef[] {
  const organizationAccounts = accounts.filter((account) =>
    account.reference.startsWith("Organization/"),
  );

  const byReference = new Map<string, ResourceRef>();
  for (const account of organizationAccounts) {
    upsertReferenceByReference(byReference, account);
  }

  return Array.from(byReference.values());
}
